/**
 * Küresel arama ekranı — tek kutu, dört tür.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ARAMANIN İŞE YARAMASI İÇİN ÜÇ ŞART
 *
 * 1. AKSANSIZ YAZINCA BULMALI. Kimse telefon klavyesinde "Şırnak" yazmaz;
 *    normalizasyon sunucuda (ölçülerek karar verildi — `routes/search.ts`).
 * 2. SONUÇ NEREYE GİDECEĞİNİ SÖYLEMELİ. Bulunan pano yalnız listelenirse
 *    kullanıcı onu ayrıca aramak zorunda kalır; her sonuç kendi ekranına
 *    bağlanıyor.
 * 3. KIRPMA GÖRÜNÜR OLMALI. "23 sonuçtan 10'u gösteriliyor" — sessizce 10
 *    göstermek, kullanıcıya aradığının olmadığını düşündürür (liste
 *    ekranlarındaki tavan uyarısının aynısı).
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { AramaKutusu, useAramaYazici } from './parts.tsx';
import { ApiError, istek } from '../lib/api.ts';
import { Button } from '../design/Button.tsx';
import { Card, CardBody } from '../design/Card.tsx';
import { EmptyState, SkeletonList } from '../design/Feedback.tsx';
import { PageBody, PageHeader } from '../shell/AppShell.tsx';

/** Sunucuyla aynı alt sınır — istemci boşuna istek atmasın. */
const EN_AZ_UZUNLUK = 2;

interface SahaSonucu {
  readonly id: number;
  readonly name: string;
  readonly addr_code: string;
  readonly site_code: string | null;
  readonly city: string | null;
  readonly district: string | null;
}

interface PanoSonucu {
  readonly id: number;
  readonly serial_number: string;
  readonly site_id: number;
  readonly site_name: string;
  readonly connection_mode: 'public' | 'vpn';
  readonly adres: string | null;
}

interface TankSonucu {
  readonly id: number;
  readonly serial_number: string;
  readonly site_id: number | null;
  readonly site_name: string | null;
  readonly tank_number: number | null;
  readonly capacity_m3: number;
  readonly brand: string | null;
}

interface MusteriSonucu {
  readonly id: number;
  readonly title: string;
  readonly customer_code: string;
  readonly site_count: number;
}

interface AramaCevabi {
  readonly sites: readonly SahaSonucu[];
  readonly panels: readonly PanoSonucu[];
  readonly tanks: readonly TankSonucu[];
  readonly customers: readonly MusteriSonucu[];
  readonly counts: {
    readonly sites: number;
    readonly panels: number;
    readonly tanks: number;
    readonly customers: number;
  };
  readonly limit: number;
}

export function Arama(): React.JSX.Element {
  const { q } = useSearch({ from: '/korumali/ara' });
  const navigate = useNavigate();

  const yaz = useCallback(
    (deger: string | undefined) => {
      void navigate({
        to: '/ara',
        search: deger === undefined ? {} : { q: deger },
        replace: true,
      });
    },
    [navigate],
  );
  const aramaDegisti = useAramaYazici(yaz);

  const terim = q ?? '';
  const yeterli = terim.trim().length >= EN_AZ_UZUNLUK;

  const sorgu = useQuery({
    queryKey: ['arama', terim],
    queryFn: () => istek<AramaCevabi>(`/search?q=${encodeURIComponent(terim.trim())}`),
    // Kısa terimde istek HİÇ atılmıyor: sunucu da reddederdi, ama boşuna
    // gidip 400 almak ekranda bir an hata gösterirdi.
    enabled: yeterli,
  });

  const toplam =
    sorgu.data === undefined
      ? 0
      : sorgu.data.counts.sites +
        sorgu.data.counts.panels +
        sorgu.data.counts.tanks +
        sorgu.data.counts.customers;

  return (
    <>
      <PageHeader title="Ara" description="Saha, pano, tank ve müşteri" />
      <AramaKutusu
        deger={terim}
        degisti={aramaDegisti}
        ipucu="Ad, kod, seri numarası veya IP adresi — Türkçe karakter yazmanız gerekmiyor"
      />

      <PageBody>
        {!yeterli && (
          <EmptyState
            title={terim === '' ? 'Aramak için yazın' : 'Biraz daha yazın'}
            description={
              terim === ''
                ? 'Saha adı, tesisat kodu, pano seri numarası veya IP adresi arayabilirsiniz.'
                : `En az ${EN_AZ_UZUNLUK} karakter gerekli.`
            }
          />
        )}

        {yeterli && sorgu.isPending && <SkeletonList rows={3} />}

        {yeterli && sorgu.isError && (
          <EmptyState
            title="Arama yapılamadı"
            description={
              sorgu.error instanceof ApiError
                ? sorgu.error.message
                : 'Sunucuya ulaşılamadı. Tekrar deneyin.'
            }
            action={<Button onClick={() => void sorgu.refetch()}>Tekrar dene</Button>}
          />
        )}

        {yeterli && sorgu.data !== undefined && (
          <div className="flex flex-col gap-5">
            {/* Sonuç sayısı ekran okuyucuya DUYURULUYOR — görsel liste
                değişimini görmeyen kullanıcı da kaç sonuç olduğunu bilir. */}
            <p role="status" className="text-sm text-ink-muted">
              {toplam === 0 ? `"${terim}" için sonuç yok` : `${toplam} sonuç bulundu`}
            </p>

            {toplam === 0 ? (
              <EmptyState
                title="Eşleşen kayıt yok"
                description="Farklı bir ad, kod veya IP deneyin."
              />
            ) : (
              <>
                <Grup
                  baslik="Sahalar"
                  gosterilen={sorgu.data.sites.length}
                  toplam={sorgu.data.counts.sites}
                >
                  {sorgu.data.sites.map((saha) => (
                    <SonucKarti
                      key={saha.id}
                      to="/sahalar/$sahaId"
                      params={{ sahaId: String(saha.id) }}
                      baslik={saha.name}
                      alt={[saha.addr_code, saha.site_code, saha.city, saha.district]}
                    />
                  ))}
                </Grup>

                <Grup
                  baslik="Panolar"
                  gosterilen={sorgu.data.panels.length}
                  toplam={sorgu.data.counts.panels}
                >
                  {sorgu.data.panels.map((pano) => (
                    <SonucKarti
                      key={pano.id}
                      to="/panolar/$panoId/duzenle"
                      params={{ panoId: String(pano.id) }}
                      baslik={pano.serial_number}
                      alt={[
                        pano.site_name,
                        pano.adres,
                        pano.connection_mode === 'vpn' ? 'VPN' : 'Genel ağ',
                      ]}
                    />
                  ))}
                </Grup>

                <Grup
                  baslik="Tanklar"
                  gosterilen={sorgu.data.tanks.length}
                  toplam={sorgu.data.counts.tanks}
                >
                  {sorgu.data.tanks.map((tank) => (
                    <SonucKarti
                      key={tank.id}
                      to="/tanklar/$tankId/duzenle"
                      params={{ tankId: String(tank.id) }}
                      baslik={tank.serial_number}
                      alt={[
                        // Depodaki tankın sahası yok — "—" yerine açık söyle.
                        tank.site_name ?? 'Depoda',
                        tank.tank_number === null ? null : `Tank ${String(tank.tank_number)}`,
                        `${tank.capacity_m3} m³`,
                        tank.brand,
                      ]}
                    />
                  ))}
                </Grup>

                <Grup
                  baslik="Müşteriler"
                  gosterilen={sorgu.data.customers.length}
                  toplam={sorgu.data.counts.customers}
                >
                  {sorgu.data.customers.map((musteri) => (
                    <SonucKarti
                      key={musteri.id}
                      to="/sahalar"
                      // Müşterinin kendi ekranı yok; sahaları filtreli
                      // listeye gidiyor — sonuç bir çıkmaza değil işe yarar
                      // bir görünüme bağlanıyor.
                      search={{ musteri: musteri.id }}
                      baslik={musteri.title}
                      alt={[musteri.customer_code, `${musteri.site_count} saha`]}
                    />
                  ))}
                </Grup>
              </>
            )}
          </div>
        )}
      </PageBody>
    </>
  );
}

/** Boş grup HİÇ çizilmiyor — dört başlıklı boş ekran gürültüdür. */
function Grup({
  baslik,
  gosterilen,
  toplam,
  children,
}: {
  readonly baslik: string;
  readonly gosterilen: number;
  readonly toplam: number;
  readonly children: React.ReactNode;
}): React.JSX.Element | null {
  if (toplam === 0) return null;

  return (
    <section aria-labelledby={`grup-${baslik}`}>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h2 id={`grup-${baslik}`} className="text-sm font-semibold text-ink">
          {baslik}
        </h2>
        {/* KIRPMA GÖRÜNÜR: "23 sonuçtan 10'u". */}
        <span className="text-xs text-ink-muted">
          {gosterilen < toplam ? `${toplam} sonuçtan ${gosterilen}'i` : `${toplam} sonuç`}
        </span>
      </div>
      <ul className="flex flex-col gap-2">{children}</ul>
    </section>
  );
}

type SonucProps = {
  readonly baslik: string;
  readonly alt: readonly (string | null)[];
} & (
  | {
      readonly to: '/sahalar/$sahaId';
      readonly params: { sahaId: string };
      readonly search?: never;
    }
  | {
      readonly to: '/panolar/$panoId/duzenle';
      readonly params: { panoId: string };
      readonly search?: never;
    }
  | {
      readonly to: '/tanklar/$tankId/duzenle';
      readonly params: { tankId: string };
      readonly search?: never;
    }
  | { readonly to: '/sahalar'; readonly search: { musteri: number }; readonly params?: never }
);

function SonucKarti(props: SonucProps): React.JSX.Element {
  const { baslik, alt } = props;
  const satir = alt.filter((x): x is string => x !== null && x !== '').join(' · ');

  return (
    <li>
      <Card>
        <CardBody>
          {/*
           * Kartın TAMAMI bağlantı: mobilde dokunma hedefi büyük olsun.
           * İç içe bağlantı yok, o yüzden burada güvenli.
           */}
          <Link
            {...(props.to === '/sahalar'
              ? { to: props.to, search: props.search }
              : props.to === '/sahalar/$sahaId'
                ? { to: props.to, params: props.params }
                : props.to === '/panolar/$panoId/duzenle'
                  ? { to: props.to, params: props.params }
                  : { to: props.to, params: props.params })}
            className="block"
          >
            <span className="font-medium text-ink">{baslik}</span>
            {satir !== '' && <span className="mt-0.5 block text-sm text-ink-muted">{satir}</span>}
          </Link>
        </CardBody>
      </Card>
    </li>
  );
}
